import dir from "@/styles/modules/directions.module.scss";
import * as Api from "@api";
import * as C from "@components";
import React from "react";
import VadImage from "vadimages-nextjs-image-optimizer";
import {stripHtml, truncateAlt} from "@/helpers/alt";

export const getFillingCappingDirectionsTableKeys = <
  TableKeyPrefixes extends readonly string[]
>(
  prefixes: TableKeyPrefixes
): readonly Api.ContentKey[] => {
  return [
    ...prefixes.map((prefix) => `${prefix}.headline`),
    ...prefixes.map((prefix) => `${prefix}.body`),
  ] as const;
};

interface FillingCappingDirectionsListItem {
  imageSrc: string;
  imageAlt?: string;
}

export interface FillingCappingDirectionsProps<
  TableKeyPrefixes extends readonly string[],
  ListKeys extends string
> {
  titleProps: React.ComponentProps<typeof C.PageContent>;
  footerContent?: React.ReactNode;
  tipContent?: React.ReactNode;
  listItems: { [Key in ListKeys]: FillingCappingDirectionsListItem };
  pageContent: Api.Content<
    | Api.ConcatenatedKeys<TableKeyPrefixes[number], "headline" | "body">
    | ListKeys
  >;
  tableKeyPrefixes: TableKeyPrefixes;
  customTableValues?: string[];
}

export const FillingCappingDirections = <
  TableKeyPrefixes extends readonly string[],
  ListKeys extends string
>({
  footerContent,
  tipContent,
  listItems,
  pageContent,
  tableKeyPrefixes,
  titleProps,
  customTableValues,
}: FillingCappingDirectionsProps<
  TableKeyPrefixes,
  ListKeys
>): React.FunctionComponentElement<
  FillingCappingDirectionsProps<TableKeyPrefixes, ListKeys>
> => {
  const pc = pageContent as Record<string, Api.ContentItem>;

  return (
    <section className={dir.directions_block}>
      <div className="container">
        <C.PageContent {...titleProps} typography={C.Typography.H2} />
        <table>
          <thead>
            <tr>
              {tableKeyPrefixes.map((keyPrefix) => (
                <th key={keyPrefix}>
                  <C.PageContent {...pc[`${keyPrefix}.headline`]} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {customTableValues
                ? customTableValues.map((keyPrefix) => (
                    <td key={keyPrefix}>
                      <p className="p2">{keyPrefix}</p>
                    </td>
                  ))
                : tableKeyPrefixes.map((keyPrefix) => (
                    <td key={keyPrefix}>
                      <C.PageContent
                        {...pc[`${keyPrefix}.body`]}
                        typography={C.Typography.P2}
                      />
                    </td>
                  ))}
            </tr>
          </tbody>
        </table>
        {tipContent}
        <ul className={dir.directions_block__list}>
          {Object.entries(listItems).map((entry, index) => {
            const [key, { imageAlt, imageSrc }] = entry as [
              ListKeys,
              FillingCappingDirectionsListItem
            ];

            const stepName = (imageAlt && imageAlt.trim()) || stripHtml(pc[key]?.data);
            const computedAlt = stepName
              ? truncateAlt(`Step ${index + 1}: ${stepName}`, 125)
              : "";

            return (
              <li key={key}>
                <figure>
                  <VadImage
                    src={imageSrc}
                    width={100}
                    height={100}
                    alt={computedAlt}
                  />
                  <figcaption>
                    <C.PageContent {...pc[key]} typography={C.Typography.P2} />
                  </figcaption>
                </figure>
              </li>
            );
          })}
        </ul>
        {footerContent}
      </div>
    </section>
  );
};
